import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Monitor } from 'lucide-react';

export default function AgentPickerPopover({
  isOpen,
  onClose,
  onSelect,
  agents,
  actionLabel = 'send designs to',
  align = 'left',
}) {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const alignClass = align === 'right' ? 'right-0' : 'left-0';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={popoverRef}
          initial={{ opacity: 0, y: -8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -8, scale: 0.98 }}
          transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
          className={`absolute top-full mt-2 ${alignClass} z-50 w-72 max-w-[calc(100vw-2rem)] rounded-2xl bg-white dark:bg-elevated-dark-1 border border-gray-200 dark:border-white/10 shadow-soft-lg overflow-hidden`}
          role="menu"
        >
          <div className="px-4 py-3 border-b border-gray-100 dark:border-white/5">
            <p className="text-caption uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
              Choose Media Station
            </p>
            <p className="text-body-sm text-gray-700 dark:text-gray-300 mt-0.5">
              Which station to {actionLabel}?
            </p>
          </div>

          <div className="max-h-72 overflow-y-auto py-1">
            {agents.map((agent) => (
              <button
                key={agent.agent_id}
                onClick={() => onSelect(agent.agent_id)}
                role="menuitem"
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              >
                <div className="flex items-center min-w-0">
                  <Monitor className="w-4 h-4 mr-2.5 flex-shrink-0 text-gray-600 dark:text-gray-300" />
                  <span className="text-body-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {agent.description || `Media Station ${agent.agent_id.slice(0, 8)}`}
                  </span>
                </div>
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="inline-block w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-caption text-gray-500 dark:text-gray-400">Online</span>
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
